import React from 'react';
import { Link } from 'react-router-dom';

import { Card } from '@/design-system/card';
import { Skeleton } from '@/design-system/skeleton';

import { ZoneBar, ZoneStatusBadge } from '../../components/OccupancyBadge';
import { fmtQty } from '../../format';
import {
  fmtAmount,
  fmtPct,
  usedOfCapacity,
  UTILIZATION_BASIS,
  zoneThresholdsLegend,
} from '../../occupancy';
import { WAREHOUSE_PATHS } from '../../paths';
import type {
  LocationUtilizationHub,
  LocationUtilizationPayload,
  UtilizationBasis,
} from '../../types';

/** "Staging, Karantina tidak dihitung di angka hub: staging / karantina". */
export const excludedNote = (
  excluded: LocationUtilizationPayload['excluded']
): string =>
  `${excluded.map((e) => e.name).join(', ')} tidak dihitung di angka hub: staging / karantina`;

/** The Storage page narrowed to the bins without a capacity. */
export const STORAGE_UNSET_LINK = `${WAREHOUSE_PATHS.storage}?unset=1`;

/** "3 bin belum punya kapasitas volume — tidak ikut dihitung · 0,4 m³ stok di bin tanpa kapasitas". */
export const unsetNote = (
  hub: Pick<LocationUtilizationHub, 'binsWithoutCapacity' | 'usedWithoutCapacity'>,
  basis: UtilizationBasis
): string | null => {
  const parts: string[] = [];
  if (hub.binsWithoutCapacity > 0) {
    parts.push(
      `${fmtQty(hub.binsWithoutCapacity)} bin belum punya kapasitas ${UTILIZATION_BASIS[basis].noun} — tidak ikut dihitung`
    );
  }
  if (hub.usedWithoutCapacity > 0) {
    parts.push(
      `${fmtAmount(hub.usedWithoutCapacity, basis)} stok di bin tanpa kapasitas`
    );
  }
  return parts.length ? parts.join(' · ') : null;
};

/**
 * The hub in one number on the chosen basis: how much of the capacity of its
 * storage locations is used, how much room is left, the status with its rule
 * as tooltip, what is not counted (bins without capacity, SKUs without
 * dimensions, staging / quarantine) and the legend.
 */
export const HubOccupancyCard: React.FC<{
  hubName: string;
  data: LocationUtilizationPayload | null;
  /** Picked client: `used` counts only that client's stock. */
  clientName?: string | null;
}> = ({ hubName, data, clientName }) => {
  if (!data) {
    return (
      <Card padding="none" className="p-5" aria-busy="true" data-testid="occ-hub-loading">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="mt-3 h-9 w-24" />
        <Skeleton className="mt-4 h-2 w-full" />
      </Card>
    );
  }
  const { hub, thresholds, excluded, basis } = data;
  const { unit } = UTILIZATION_BASIS[basis];
  const unset = unsetNote(hub, basis);
  return (
    <Card padding="none" className="p-5" data-testid="occ-hub">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-text-sub-600">
            <i className="ri-building-2-line text-[14px]" aria-hidden />
            <span className="truncate">{hubName}</span>
          </p>
          <p className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span
              className="text-[36px] font-extrabold leading-none tracking-tight tabular-nums text-text-strong-950"
              data-testid="occ-hub-pct"
            >
              {fmtPct(hub.pct)}
            </span>
            <span className="text-[13px] text-text-sub-600">
              terpakai dari kapasitas {unit}
            </span>
            <ZoneStatusBadge
              status={hub.status}
              thresholds={thresholds}
              basis={basis}
              testId="occ-hub-status"
            />
          </p>
          <p
            className="mt-2 text-[13px] tabular-nums text-text-sub-600"
            data-testid="occ-hub-used"
          >
            {usedOfCapacity(hub, basis)}
          </p>
        </div>
        <div className="flex flex-col gap-3 md:items-end">
          <div className="md:text-right" data-testid="occ-hub-free">
            <p className="text-[20px] font-bold leading-tight tracking-tight tabular-nums text-text-strong-950">
              {fmtAmount(hub.free, basis)} tersisa
            </p>
            <p className="text-[12px] text-text-sub-600">
              ruang yang masih bisa diisi
            </p>
          </div>
        </div>
      </div>

      <ZoneBar
        pct={hub.pct}
        status={hub.status}
        size="lg"
        label={`Utilisasi ${hubName}`}
        className="mt-4"
      />

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[11px] text-text-sub-600">
        <span data-testid="occ-thresholds">
          {zoneThresholdsLegend(thresholds)}
        </span>
        {excluded.length > 0 && (
          <span data-testid="occ-excluded">{excludedNote(excluded)}</span>
        )}
      </div>
      {unset && (
        <p
          className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-text-strong-950"
          data-testid="occ-hub-unset"
        >
          <i
            className="ri-error-warning-line text-[14px] text-(--state-warning-base)"
            aria-hidden
          />
          <span>{unset}</span>
          {hub.binsWithoutCapacity > 0 && (
            <Link
              to={STORAGE_UNSET_LINK}
              className="font-semibold text-accent underline-offset-4 hover:underline"
              data-testid="occ-hub-unset-link"
            >
              Atur di Storage
            </Link>
          )}
        </p>
      )}
      {basis === 'volume' && hub.hasNoDim && (
        <p
          className="mt-1 text-[12px] text-text-sub-600"
          data-testid="occ-hub-nodim"
        >
          Ada SKU tanpa dimensi — volumenya dihitung 0.
        </p>
      )}
      {clientName && (
        <p
          className="mt-1 text-[11px] text-text-sub-600"
          data-testid="occ-client-scope"
        >
          Terpakai hanya menghitung stok {clientName}; kapasitas dan tren tetap
          seluruh hub.
        </p>
      )}
    </Card>
  );
};
